import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import { styled } from 'styled-components';

import {
  FiatCurrency,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandL,
  BrandS,
  BrandXXS,
} from '@sorare/core/src/atoms/typography';
import { ConfirmSlider } from '@sorare/core/src/atoms/ui/ConfirmSlider';
import { missionBackground } from '@sorare/core/src/components/missions/style';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, marketCredits } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  getRewardImage,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { TaskResultsPreClaimStoryTemplate } from '#src/components/missions/stories/TaskResultsPreClaimStoryTemplate/index.tsx';

import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import type {
  ChooseYourRewardStory_alternativeRewardConfig,
  ChooseYourRewardStory_rewardConfig,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  ${missionBackground}
  height: 100%;
  width: 100%;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;

const ButtonsWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  animation-delay: 1.35s;
`;

const OptionButton = styled(ButtonBase)`
  background-color: var(--c-nd-50);
  color: var(--c-white);
  height: 48px;
  padding: 0px 20px;
  border-radius: var(--intermediate-unit);
  font: var(--t-brand-s);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  &.active,
  &:hover,
  &:focus {
    background: var(--c-nd-150);
  }
  min-width: 150px;

  &.selected {
    background: var(--c-nd-150);
    border: 1px solid var(--c-white);
  }

  img {
    height: 1.8em;
    object-fit: contain;
  }
`;

const RewardInfoBanner = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--triple-unit);
  padding: var(--intermediate-unit) var(--quadruple-unit)
    var(--intermediate-unit) var(--intermediate-unit);

  --border-gradient: var(--c-gradient-discount);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  ${borderGradientStyle}
`;

const useFormatRewardConfigs = (
  rewardConfig:
    | ChooseYourRewardStory_rewardConfig
    | ChooseYourRewardStory_alternativeRewardConfig['rewardConfigs'][number]
) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { formatNumber } = useIntl();

  const icon = getRewardImage(rewardConfig);

  if (isType(rewardConfig, 'MonetaryRewardConfig')) {
    return {
      type: 'MonetaryRewardConfig',
      icon,
      percentageDiscount: undefined,
      total: formatNumber(toMonetaryAmount(rewardConfig.monetaryAmount).usd, {
        style: 'currency',
        currency: FiatCurrency.USD,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      }),
    };
  }

  if (isType(rewardConfig, 'ConversionCreditRewardConfig')) {
    return {
      type: 'ConversionCreditRewardConfig',
      icon,
      percentageDiscount: rewardConfig.percentageDiscount,
      total: formatNumber(toMonetaryAmount(rewardConfig.maxDiscount).usd, {
        style: 'currency',
        currency: FiatCurrency.USD,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      }),
    };
  }

  if (isType(rewardConfig, 'CardShardRewardConfig')) {
    return {
      type: 'CardShardRewardConfig',
      percentageDiscount: undefined,
      icon,
      total: rewardConfig.quantity,
    };
  }

  if (isType(rewardConfig, 'InGameCurrencyRewardConfig')) {
    return {
      type: 'InGameCurrencyRewardConfig',
      percentageDiscount: undefined,
      icon,
      total: rewardConfig.amount,
    };
  }

  return {
    icon,
    total: 0,
  };
};

type Props = {
  rewardConfigs: ChooseYourRewardStory_rewardConfig[];
  alternativeRewardConfigs: ChooseYourRewardStory_alternativeRewardConfig[];
  setSignedAlternativeRewardConfigs: (
    signedAlternativeRewardConfigs: string | undefined
  ) => void;
  onConfirm: () => Promise<void>;
};
export const ChooseYourRewardStory = ({
  rewardConfigs,
  alternativeRewardConfigs,
  setSignedAlternativeRewardConfigs,
  onConfirm,
}: Props) => {
  const { sport } = useSportContext();
  const [percentageSwipped, setPercentageSwipped] = useState(0);
  const [selectedReward, setSelectedReward] = useState<'main' | 'alternative'>(
    'main'
  );

  const mainRewardConfig = mergeRewardsConfig(rewardConfigs)[0];
  const alternativeRewardConfig = mergeRewardsConfig(
    alternativeRewardConfigs[0].rewardConfigs
  )[0];

  const { icon: mainRewardIcon, total: mainRewardTotal } =
    useFormatRewardConfigs(mainRewardConfig);
  const {
    icon: alternativeRewardIcon,
    total: alternativeRewardTotal,
    type: alternativeRewardType,
    percentageDiscount: alternativeRewardPercentageDiscount,
  } = useFormatRewardConfigs(alternativeRewardConfig);

  return (
    <Root
      style={
        {
          '--card-avatar-size': 'calc(var(--unit) * 8)',
          '--rgb-color': 'var(--c-rgb-green-400)',
          '--opacity': '0.4',
        } as CSSProperties
      }
    >
      <TaskResultsPreClaimStoryTemplate
        featuredContent={
          <Vertical gap={2} center>
            <Horizontal gap={1.5}>
              <Gift size={3} />
            </Horizontal>
            <BrandL>
              <FormattedMessage
                defaultMessage="Choose your{br}reward"
                values={{
                  br: <br />,
                }}
              />
            </BrandL>
          </Vertical>
        }
        title={
          <Horizontal gap={1.5}>
            <OptionButton
              className={classNames({ selected: selectedReward === 'main' })}
              onClick={() => {
                setSelectedReward('main');
                setSignedAlternativeRewardConfigs(undefined);
              }}
            >
              <img src={mainRewardIcon} alt="" />
              {mainRewardTotal}
              {isType(mainRewardConfig, 'CardShardRewardConfig') &&
                !!mainRewardConfig.flavour?.iconUrl &&
                sport === Sport.FOOTBALL && (
                  <img src={mainRewardConfig.flavour.iconUrl} alt="" />
                )}
            </OptionButton>
            <BrandXXS color="var(--c-nd-700)" as="span">
              <FormattedMessage {...glossary.or} />
            </BrandXXS>
            <OptionButton
              className={classNames({
                selected: selectedReward === 'alternative',
              })}
              onClick={() => {
                setSelectedReward('alternative');
                setSignedAlternativeRewardConfigs(
                  alternativeRewardConfigs[0].signedAlternativeRewardConfigs
                );
              }}
            >
              <img src={alternativeRewardIcon} alt="" />
              {alternativeRewardTotal}
              {isType(alternativeRewardConfig, 'CardShardRewardConfig') &&
                !!alternativeRewardConfig.flavour?.iconUrl &&
                sport === Sport.FOOTBALL && (
                  <img src={alternativeRewardConfig.flavour.iconUrl} alt="" />
                )}
            </OptionButton>
          </Horizontal>
        }
        subtitle={null}
      />
      <ButtonsWrapper>
        {selectedReward === 'alternative' &&
          alternativeRewardType === 'ConversionCreditRewardConfig' && (
            <RewardInfoBanner>
              <Horizontal>
                <img
                  src={alternativeRewardIcon}
                  alt=""
                  width={36}
                  height={36}
                />
                <Vertical gap={0.5}>
                  <BrandS>
                    <FormattedMessage {...marketCredits.marketCredits} />
                  </BrandS>
                  <BodyS color="var(--c-nd-600)">
                    <FormattedMessage
                      {...marketCredits.description}
                      values={{
                        percentage: alternativeRewardPercentageDiscount,
                        maxDiscount: alternativeRewardTotal,
                      }}
                    />
                  </BodyS>
                </Vertical>
              </Horizontal>
            </RewardInfoBanner>
          )}
        <ConfirmSlider
          onConfirm={onConfirm}
          onScroll={scroll => {
            setPercentageSwipped(scroll);
          }}
          ctaMessage={
            <span style={{ opacity: 1 - percentageSwipped }}>
              <FormattedMessage defaultMessage="Swipe to confirm" />
            </span>
          }
        />
      </ButtonsWrapper>
    </Root>
  );
};

const fragments = {
  rewardConfig: gql`
    fragment ChooseYourRewardStory_rewardConfig on AnyRewardConfigInterface {
      id
      ...mergeRewardsConfig_anyRewardConfigInterface
      ...getRewardImage_anyRewardConfigInterface
      ... on ConversionCreditRewardConfig {
        id
        percentageDiscount
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
      }
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on CardShardRewardConfig {
        id
        quantity
        flavour {
          slug
          iconUrl
        }
      }
    }
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<ChooseYourRewardStory_rewardConfig>,
};

ChooseYourRewardStory.fragments = {
  rewardConfig: fragments.rewardConfig,
  alternativeRewardConfig: gql`
    fragment ChooseYourRewardStory_alternativeRewardConfig on AlternativeRewardConfig {
      signedAlternativeRewardConfigs
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
      }
    }
    ${fragments.rewardConfig}
  ` as TypedDocumentNode<ChooseYourRewardStory_alternativeRewardConfig>,
};
